import React from 'react';
import { View, StyleSheet, Pressable, useWindowDimensions } from 'react-native';
import { Text, Chip } from 'react-native-paper';
import { Color } from '@/GlobalStyles';
import { DossierStatus } from './DossierStatusIndicator';
import { useTranslation } from 'react-i18next';

interface DossierStatusFilterProps {
  selectedStatuses: DossierStatus[];
  onStatusToggle: (status: DossierStatus) => void;
  onReset?: () => void;
}

const DossierStatusFilter: React.FC<DossierStatusFilterProps> = ({
  selectedStatuses,
  onStatusToggle,
  onReset,
}) => {
  const { t } = useTranslation();
  const { width: screenWidth } = useWindowDimensions();
  const isMobile = screenWidth < 768;

  const statuses: { value: DossierStatus; label: string; color: string }[] = [
    { value: 'ORANGE', label: t('En cours'), color: '#FF9800' },
    { value: 'VERT', label: t('Finalisés'), color: '#4CAF50' },
    { value: 'GRIS', label: t('Archivés'), color: '#9E9E9E' },
  ];

  return (
    <View style={[styles.container, isMobile && styles.containerMobile]}>
      <View style={[!isMobile && styles.filterRow, isMobile && styles.filterRowMobile]}>
        <Text style={styles.label}>{t('Filtrer par statut')} :</Text>
        <View style={styles.chipsContainer}>
          {statuses.map((status) => (
            <Chip
              key={status.value}
              mode={'outlined'}
              selected={selectedStatuses.includes(status.value)}
              onPress={() => onStatusToggle(status.value)}
              style={
                selectedStatuses.includes(status.value) && {
                  backgroundColor: status.color + '20',
                }
              }
              textStyle={{
                color: selectedStatuses.includes(status.value) ? status.color : Color.colorGray_150,
              }}
            >
              {isMobile && status.value === 'ORANGE'
                ? t('En cours')
                : isMobile && status.value === 'VERT'
                  ? t('Finalisés')
                  : status.label}
            </Chip>
          ))}
        </View>
        {onReset && selectedStatuses.length > 0 && !isMobile && (
          <Pressable onPress={onReset} style={styles.resetButton}>
            <Text style={styles.resetText}>{t('Réinitialiser')}</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Color.colorGray_100,
  },
  containerMobile: {
    paddingVertical: 8,
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  filterRowMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: Color.MainNoir,
  },
  chipsContainer: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  resetButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  resetText: {
    color: Color.MainBleu,
    fontSize: 14,
    fontWeight: '500',
  },
});

export default DossierStatusFilter;
